import { createClient } from '@supabase/supabase-js';

// Shared Supabase singleton.
//
// Throwing at module evaluation when env vars are missing white-screens every
// app that imports this — and no error boundary upstream can catch it because
// the error fires before React even mounts. Fail soft instead: log loudly,
// expose a sentinel client that errors clearly on every call so missing env
// isn't silently masked, and let app-level error boundaries render an
// "infrastructure unavailable" screen. (TAS-93)
const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const MISSING_ENV_MESSAGE =
  'Supabase env missing: set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY for this app. ' +
  'See .env.example. All Supabase calls will fail until this is fixed.';

function makeSentinelClient() {
  if (typeof console !== 'undefined' && console.error) {
    console.error(MISSING_ENV_MESSAGE);
  }
  const reject = () => Promise.reject(new Error(MISSING_ENV_MESSAGE));
  const ch = () => ({
    on: () => ch(),
    subscribe: reject,
    unsubscribe: reject,
  });
  // Mimic the surface enough that callers either get a typed Promise rejection
  // (so try/catch + .then/.catch paths flag it) or a no-op subscription.
  const query = new Proxy(
    {},
    {
      get: (_t, prop) => {
        if (prop === 'then' || prop === 'catch' || prop === 'finally') return reject;
        return () => query;
      },
    }
  );
  return {
    from: () => query,
    rpc: reject,
    auth: {
      getUser: reject,
      getSession: reject,
      signInWithPassword: reject,
      signInWithOtp: reject,
      signOut: reject,
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    },
    functions: { invoke: reject },
    storage: { from: () => ({ upload: reject, download: reject, getPublicUrl: () => ({ data: { publicUrl: '' } }) }) },
    channel: ch,
    removeChannel: () => {},
    realtime: { setAuth: () => {} },
    __isSupabaseSentinel: true,
  };
}

// Hardened auth defaults — keep session storage explicit and ride the
// localStorage default, but disable URL-based session detection on non-browser
// runtimes (SSR/Node) where window.location can't be read safely.
const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';

// TAS-176 (corrected re-do): reconnect the shared realtime socket when the
// heartbeat reports it dropped. Deliberately WITHOUT `worker: true` — that made
// a blob Web Worker the ONLY heartbeat driver, and our CSP blocks blob: workers,
// so the worker died silently and wedged realtime for every channel (the
// reverted first attempt broke the 3-player E2E). The normal in-page heartbeat
// stays; this only adds a best-effort reconnect nudge on a reported drop. The
// callback closes over the local `client`, not the exported binding.
function makeRealClient() {
  const client = createClient(url, anonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: isBrowser,
      flowType: 'pkce',
    },
    global: { headers: { 'x-client-info': 'tasted-monorepo' } },
    realtime: {
      heartbeatCallback: (status) => {
        if (status !== 'disconnected') return;
        try {
          client.realtime.connect();
        } catch {
          /* best-effort — per-channel reconnect (resilientChannel) still applies */
        }
      },
    },
  });
  return client;
}

export const supabase = url && anonKey ? makeRealClient() : makeSentinelClient();

export const supabaseConfigured = Boolean(url && anonKey);
